import { type MongoAbility, type RawRuleOf, createMongoAbility } from '@casl/ability';
import { unpackRules } from '@casl/ability/extra';
import type { PackRule } from '@casl/ability/extra';
import { AbilityProvider } from '@casl/react';
import { type ReactNode, useMemo } from 'react';
import { AccessControlContext } from './AccessControlContext';

/** Packed CASL rules as they travel over the wire from `getPermissions`. */
export type PackedRules = PackRule<RawRuleOf<MongoAbility>>[];

export interface AccessControlProviderProps {
  /** Packed CASL rules for the current user, or `undefined` while loading. */
  rules: PackedRules | undefined;
  /** True while the rules are still being fetched — drives deny-all defaulting. */
  isLoading?: boolean;
  children: ReactNode;
}

/**
 * Hydrates a CASL ability from packed rules and publishes it to descendants. Until rules arrive
 * the ability denies everything, so the UI never flashes privileged affordances. Populates both
 * `@casl/react`'s AbilityProvider (`<Can>`/`useAbility`) and AccessControlContext (`isLoading`).
 */
export function AccessControlProvider({
  rules,
  isLoading = false,
  children,
}: AccessControlProviderProps) {
  const ability = useMemo(() => createMongoAbility(unpackRules(rules ?? [])), [rules]);

  const value = useMemo(() => ({ ability, isLoading }), [ability, isLoading]);

  return (
    <AccessControlContext.Provider value={value}>
      <AbilityProvider value={ability}>{children}</AbilityProvider>
    </AccessControlContext.Provider>
  );
}
